(function () {
  function updateHeaderGroupHeight() {
    const headerGroup = document.getElementById('header-group');
    if (!headerGroup) return;

    let height = 0;
    for (const child of headerGroup.children) {
      if (child instanceof HTMLElement) {
        height += child.offsetHeight;
      }
    }

    document.body.style.setProperty('--header-group-height', `${height}px`);
    document.body.style.setProperty('--ch-header-offset', `${height}px`);
  }

  function setupMobileNav() {
    const toggle = document.querySelector('[data-ch-nav-toggle]');
    const drawer = document.querySelector('[data-ch-nav-drawer]');
    if (!toggle || !drawer || toggle.dataset.chNavBound === '1') return;
    toggle.dataset.chNavBound = '1';

    const panel = drawer.querySelector('.ch-nav-drawer__panel');
    const closeEls = drawer.querySelectorAll('[data-ch-nav-close]');
    const links = drawer.querySelectorAll('.ch-nav-drawer__link');
    let suppressBackdropCloseUntil = 0;

    function isDrawerOpen() {
      return drawer.classList.contains('is-open');
    }

    function syncClosedState() {
      drawer.classList.remove('is-open');
      drawer.hidden = true;
      drawer.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('ch-nav-open');
    }

    function openDrawer() {
      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      suppressBackdropCloseUntil = Date.now() + 450;
      requestAnimationFrame(() => {
        drawer.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
        document.body.classList.add('ch-nav-open');
      });
    }

    function closeDrawer() {
      if (!isDrawerOpen() && drawer.hidden) return;
      drawer.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('ch-nav-open');

      const onEnd = (e) => {
        if (e.target !== panel) return;
        drawer.hidden = true;
        drawer.setAttribute('aria-hidden', 'true');
        panel?.removeEventListener('transitionend', onEnd);
      };
      panel?.addEventListener('transitionend', onEnd);

      window.setTimeout(() => {
        if (!isDrawerOpen()) {
          drawer.hidden = true;
          drawer.setAttribute('aria-hidden', 'true');
        }
      }, 320);
    }

    function handleCloseClick(e) {
      if (
        e.currentTarget instanceof HTMLElement &&
        e.currentTarget.classList.contains('ch-nav-drawer__backdrop') &&
        Date.now() < suppressBackdropCloseUntil
      ) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      closeDrawer();
    }

    if (drawer.hidden || !isDrawerOpen()) {
      syncClosedState();
    }

    toggle.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isDrawerOpen()) closeDrawer();
      else openDrawer();
    });

    closeEls.forEach((el) => {
      el.addEventListener('click', handleCloseClick);
    });

    links.forEach((link) => {
      link.addEventListener('click', closeDrawer);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isDrawerOpen()) {
        closeDrawer();
      }
    });

    window.addEventListener(
      'resize',
      () => {
        if (window.innerWidth >= 990 && isDrawerOpen()) {
          syncClosedState();
        }
      },
      { passive: true }
    );
  }

  /**
   * Scroll-up header — hide on scroll down, show fixed on scroll up.
   * Avoids layout-shift feedback loops from IntersectionObserver + fixed top checks.
   */
  function setupScrollHeader() {
    const section = document.querySelector('[data-ch-site-header]');
    const bar = document.querySelector('[data-ch-header-bar]');
    const spacer = document.querySelector('[data-ch-header-spacer]');
    if (!section || !bar || !spacer || section.dataset.chScrollBound === '1') return;
    section.dataset.chScrollBound = '1';

    const SCROLL_DELTA = 8;
    const TOP_THRESHOLD = 4;
    const SETTLE_MS = 320;

    let lastScrollTop = window.scrollY;
    let scrollRafId = null;
    let ignoreScrollUntil = 0;
    let leaveTopHeight = 0;

    function barHeight() {
      return bar.offsetHeight;
    }

    function syncBarHeightVar() {
      document.body.style.setProperty('--ch-header-bar-height', `${barHeight()}px`);
    }

    function applyLayout(state) {
      section.dataset.chStickyState = state;

      if (state === 'inactive') {
        bar.classList.remove('is-fixed', 'is-hidden');
        spacer.hidden = true;
        spacer.style.height = '0px';
        leaveTopHeight = 0;
        return;
      }

      bar.classList.add('is-fixed');
      bar.classList.toggle('is-hidden', state === 'idle');

      if (state === 'active') {
        const h = barHeight();
        spacer.hidden = false;
        spacer.style.height = `${h}px`;
      } else {
        spacer.hidden = true;
        spacer.style.height = '0px';
      }
    }

    function setStickyState(state) {
      const prev = section.dataset.chStickyState;
      if (prev === state) return;

      applyLayout(state);
      updateHeaderGroupHeight();

      if (prev === 'active' && state === 'idle') {
        ignoreScrollUntil = Date.now() + SETTLE_MS;
      } else if (prev === 'idle' && state === 'active') {
        ignoreScrollUntil = Date.now() + SETTLE_MS;
      } else if (state === 'inactive') {
        ignoreScrollUntil = Date.now() + SETTLE_MS;
      }
    }

    function hasLeftPageTop() {
      if (leaveTopHeight <= 0) {
        leaveTopHeight = barHeight();
      }
      return window.scrollY > leaveTopHeight + TOP_THRESHOLD;
    }

    function updateScrollHeader() {
      scrollRafId = null;

      const scrollTop = window.scrollY;
      const delta = scrollTop - lastScrollTop;

      if (document.body.classList.contains('ch-nav-open')) {
        if (hasLeftPageTop()) setStickyState('active');
        lastScrollTop = scrollTop;
        return;
      }

      if (Date.now() < ignoreScrollUntil) {
        lastScrollTop = scrollTop;
        return;
      }

      if (scrollTop <= TOP_THRESHOLD) {
        setStickyState('inactive');
        lastScrollTop = scrollTop;
        return;
      }

      if (!hasLeftPageTop()) {
        lastScrollTop = scrollTop;
        return;
      }

      if (delta < -SCROLL_DELTA) {
        setStickyState('active');
      } else if (delta > SCROLL_DELTA) {
        setStickyState('idle');
      }

      lastScrollTop = scrollTop;
    }

    window.addEventListener(
      'scroll',
      () => {
        if (scrollRafId !== null) return;
        scrollRafId = requestAnimationFrame(updateScrollHeader);
      },
      { passive: true }
    );

    const onResize = () => {
      syncBarHeightVar();
      leaveTopHeight = barHeight();
      const state = section.dataset.chStickyState;
      if (state === 'active') {
        spacer.style.height = `${barHeight()}px`;
      }
      if (window.scrollY <= TOP_THRESHOLD) {
        setStickyState('inactive');
      }
      updateHeaderGroupHeight();
    };

    window.addEventListener('resize', onResize, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      const barObserver = new ResizeObserver(onResize);
      barObserver.observe(bar);
    }

    syncBarHeightVar();
    leaveTopHeight = barHeight();
    applyLayout('inactive');
  }

  function init() {
    updateHeaderGroupHeight();
    setupMobileNav();
    setupScrollHeader();

    const headerGroup = document.getElementById('header-group');
    if (headerGroup && typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateHeaderGroupHeight);
      observer.observe(headerGroup);
    }

    window.addEventListener('resize', updateHeaderGroupHeight, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
